// Companion/companionAnalytics.ts
//
// The companion funnel (ORA-1118): opened → message sent → rated. Before these
// events the only send-level data was the server's trace, so nobody could say
// where students stop. Every event carries ids and kinds only — never message
// text, a page path, or anything else the student wrote.

import { createContext, useContext, useEffect, useRef } from 'react';
import { getClientPlatform } from '@/services/clientVersion';
import type {
  ChatAnchorType,
  ChatAppContextEnvelope,
  ChatSurface,
} from '@/types/chatEnvelope';
import { capture } from '@/utils/analytics';

/**
 * Which shell the student is in. Finer than the envelope's `ChatSurface`,
 * which says `drawer` for the study drawer, the floating window and the mobile
 * sheet alike.
 */
export type CompanionAnalyticsSurface =
  'chat_page' | 'drawer' | 'floating_shell' | 'mobile_sheet';

/**
 * What the companion was pointed at: the anchored item's type, `page` for a
 * page with no item on it, and `none` on the chat page or once the student
 * removed the context from the chat.
 */
export type CompanionAnchorKind = ChatAnchorType | 'page' | 'none';

/** How a message was asked. */
export type CompanionSendEntry =
  /** Typed into the composer. */
  | 'typed'
  /** A follow-up chip under an answer. */
  | 'followup_chip'
  /** A preset prompt chip on an empty thread. */
  | 'prompt_chip'
  /** Staged by a control elsewhere in the app ("Ask Ora about this"). */
  | 'staged';

/**
 * Why the companion came on screen, so a funnel can separate a deliberate open
 * from a page view. The AI desktop is the home route: it "opens" on every app
 * load and every time a window closes back to it.
 */
export type CompanionOpenTrigger =
  /** The student opened the drawer, the floating window or the sheet. */
  | 'explicit'
  /** The student navigated to the full chat page. */
  | 'navigation'
  /** The home route's desktop came on screen. */
  | 'home_landing';

/** The drawer shell's chrome, as the surface the student sees. */
export const companionSurfaceForChrome = (
  chrome: 'drawer' | 'shell' | 'sheet'
): CompanionAnalyticsSurface =>
  chrome === 'shell'
    ? 'floating_shell'
    : chrome === 'sheet'
      ? 'mobile_sheet'
      : 'drawer';

/** The envelope surface, when no shell said anything finer. */
export const companionSurfaceForEnvelope = (
  surface: ChatSurface
): CompanionAnalyticsSurface =>
  surface === 'chat_page' ? 'chat_page' : 'drawer';

export const companionAnchorKind = (
  envelope: ChatAppContextEnvelope | undefined
): CompanionAnchorKind => {
  if (!envelope || envelope.anchorDetached) return 'none';
  if (envelope.anchor) return envelope.anchor.type;

  return envelope.route === 'chat' ? 'none' : 'page';
};

/**
 * The surface every event inside one conversation view reports. Provided by
 * `ChatConversationView`, so a control deep in the thread (the rating thumbs)
 * does not need it threaded through every row.
 */
export const CompanionAnalyticsSurfaceContext =
  createContext<CompanionAnalyticsSurface | null>(null);

export const useCompanionAnalyticsSurface =
  (): CompanionAnalyticsSurface | null =>
    useContext(CompanionAnalyticsSurfaceContext);

const envelopeProps = (envelope: ChatAppContextEnvelope | undefined) => ({
  anchorKind: companionAnchorKind(envelope),
  route: envelope?.route ?? 'other',
  platform: getClientPlatform(),
});

export const captureCompanionOpened = (
  surface: CompanionAnalyticsSurface,
  trigger: CompanionOpenTrigger,
  envelope: ChatAppContextEnvelope | undefined
): void => {
  capture('companion_opened', {
    surface,
    trigger,
    ...envelopeProps(envelope),
  });
};

export const captureCompanionMessageSent = ({
  surface,
  envelope,
  entry,
  hasAttachment,
}: {
  surface: CompanionAnalyticsSurface;
  envelope: ChatAppContextEnvelope | undefined;
  entry: CompanionSendEntry;
  hasAttachment: boolean;
}): void => {
  capture('companion_message_sent', {
    surface,
    ...envelopeProps(envelope),
    entry,
    fromChip: entry === 'followup_chip' || entry === 'prompt_chip',
    hasAttachment,
  });
};

export const captureCompanionRated = ({
  rating,
  surface,
}: {
  /** `null` is the student retracting the thumb they had set. */
  rating: 'up' | 'down' | null;
  surface: CompanionAnalyticsSurface | null;
}): void => {
  capture('companion_rated', {
    rating: rating ?? 'cleared',
    surface: surface ?? 'unknown',
    platform: getClientPlatform(),
  });
};

/**
 * Report `companion_opened` each time a shell comes on screen — the drawer or
 * window opening, the page mounting — and not again while it stays open. The
 * envelope is read when it opens; later changes to it are not a new open.
 */
export const useCompanionOpenedCapture = ({
  isOpen,
  surface,
  trigger,
  envelope,
}: {
  isOpen: boolean;
  surface: CompanionAnalyticsSurface;
  trigger: CompanionOpenTrigger;
  envelope: ChatAppContextEnvelope | undefined;
}): void => {
  const envelopeRef = useRef(envelope);
  envelopeRef.current = envelope;
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      captureCompanionOpened(surface, trigger, envelopeRef.current);
    }

    wasOpen.current = isOpen;
  }, [isOpen, surface, trigger]);
};
